import { useState, useRef } from 'react';
import { Upload, X, Image as ImageIcon, Loader2, AlertCircle, ZoomIn, ImageOff } from 'lucide-react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { apiConfig } from '../amplify-config';

// Allowed file types (Requirement 9.2)
const ALLOWED_FILE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
const ALLOWED_EXTENSIONS = '.jpg,.jpeg,.png,.webp,.gif';

// Max file size: 5MB (Requirement 9.3)
const MAX_FILE_SIZE = 5 * 1024 * 1024;

interface ImageUploadProps {
  /** The card the image belongs to. Omit on the Create page; the image is then uploaded under pending/. */
  cardId?: string;
  currentImageUrl?: string;
  onImageChange: (imageUrl: string | null) => void;
}

interface UploadUrlResponse {
  uploadUrl: string;
  imageKey: string;
  cloudFrontUrl: string;
}

async function getAuthHeaders(): Promise<HeadersInit> {
  try {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();
    return {
      'Content-Type': 'application/json',
      'Authorization': token ? `Bearer ${token}` : '',
    };
  } catch {
    return {
      'Content-Type': 'application/json',
    };
  }
}

async function getUploadUrl(
  cardId: string | undefined,
  filename: string,
  contentType: string
): Promise<UploadUrlResponse> {
  const headers = await getAuthHeaders();
  // VAP-824: without a card the backend signs a key under pending/
  const endpoint = cardId
    ? `${apiConfig.adminApiUrl}/content/${cardId}/upload-url`
    : `${apiConfig.adminApiUrl}/content/upload-url`;
  const response = await fetch(endpoint, {
    method: 'POST',
    headers,
    body: JSON.stringify({ filename, contentType }),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error || 'Failed to get upload URL');
  }

  return response.json();
}

export function ImageUpload({ cardId, currentImageUrl, onImageChange }: ImageUploadProps) {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);
  const [imageError, setImageError] = useState(false);
  const [showFullPreview, setShowFullPreview] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateFile = (file: File): string | null => {
    // Validate file type (Requirement 9.2)
    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      return 'Invalid file type. Allowed: JPEG, PNG, WebP, GIF';
    }

    // Validate file size (Requirement 9.3)
    if (file.size > MAX_FILE_SIZE) {
      return `File too large. Maximum size: ${MAX_FILE_SIZE / (1024 * 1024)}MB`;
    }

    return null;
  };

  const handleFileSelect = async (file: File) => {
    setError(null);

    // Validate file
    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      // Step 1: Get presigned URL (20% progress)
      setUploadProgress(20);
      const { uploadUrl, cloudFrontUrl } = await getUploadUrl(
        cardId,
        file.name,
        file.type
      );

      // Step 2: Upload to S3 (20-90% progress)
      setUploadProgress(40);
      
      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type,
        },
      });

      if (!uploadResponse.ok) {
        throw new Error('Failed to upload image to storage');
      }

      setUploadProgress(90);

      // Step 3: Notify parent with CloudFront URL (100% progress)
      onImageChange(cloudFrontUrl);
      setUploadProgress(100);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
    // Reset input so same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    
    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleRemove = () => {
    onImageChange(null);
    setError(null);
    setImageError(false);
    setImageLoading(true);
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleImageLoad = () => {
    setImageLoading(false);
    setImageError(false);
  };

  const handleImageError = () => {
    setImageLoading(false);
    setImageError(true);
  };

  const openFullPreview = () => {
    setShowFullPreview(true);
  };

  const closeFullPreview = () => {
    setShowFullPreview(false);
  };

  return (
    <div className="space-y-3">
      {currentImageUrl ? (
        // Image preview with remove button (Requirement 9.8)
        <div className="relative rounded-md overflow-hidden border border-border bg-muted">
          {/* Loading state for image */}
          {imageLoading && !imageError && (
            <div className="absolute inset-0 flex items-center justify-center bg-muted">
              <Loader2 className="w-8 h-8 text-muted-foreground animate-spin" />
            </div>
          )}
          
          {/* Error state for broken images */}
          {imageError ? (
            <div className="w-full h-48 flex flex-col items-center justify-center bg-muted text-muted-foreground">
              <ImageOff className="w-10 h-10 mb-2" />
              <p className="text-sm">Failed to load image</p>
              <p className="text-xs text-muted-foreground mt-1">The image may have been deleted or is unavailable</p>
            </div>
          ) : (
            <img
              src={currentImageUrl}
              alt="Card image"
              className={`w-full h-48 object-cover cursor-pointer transition-opacity ${imageLoading ? 'opacity-0' : 'opacity-100'}`}
              onLoad={handleImageLoad}
              onError={handleImageError}
              onClick={openFullPreview}
            />
          )}
          
          {/* Action buttons overlay */}
          <div className="absolute top-2 right-2 flex gap-2">
            {/* Zoom button - only show when image is loaded successfully */}
            {!imageLoading && !imageError && (
              <button
                type="button"
                onClick={openFullPreview}
                className="p-1.5 bg-gray-800 bg-opacity-70 text-white rounded-full hover:bg-opacity-90 transition-colors shadow-lg"
                title="View full size"
              >
                <ZoomIn className="w-4 h-4" />
              </button>
            )}
            
            {/* Remove button */}
            <button
              type="button"
              onClick={handleRemove}
              className="p-1.5 bg-destructive text-destructive-foreground rounded-full hover:bg-destructive/90 transition-colors shadow-lg"
              title="Remove image"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          
          {/* Image URL indicator */}
          {!imageError && (
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/50 to-transparent p-2">
              <p className="text-xs text-white truncate" title={currentImageUrl}>
                {currentImageUrl.split('/').pop()}
              </p>
            </div>
          )}
        </div>
      ) : (
        // Upload zone
        <div
          onClick={handleClick}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          className={`
            relative border-2 border-dashed rounded-md p-6 text-center cursor-pointer
            transition-colors
            ${dragOver 
              ? 'border-primary bg-primary-50 dark:bg-primary-900/30' 
              : 'border-input hover:border-muted-foreground/60 hover:bg-muted/60'
            }
            ${uploading ? 'pointer-events-none opacity-75' : ''}
          `}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept={ALLOWED_EXTENSIONS}
            onChange={handleInputChange}
            disabled={uploading}
            className="hidden"
          />
          
          {uploading ? (
            <div className="space-y-3">
              <Loader2 className="w-10 h-10 mx-auto text-primary animate-spin" />
              <p className="text-sm text-muted-foreground">Uploading...</p>
              {/* Progress bar */}
              <div className="w-full bg-muted rounded-full h-2">
                <div
                  className="bg-primary h-2 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
              <p className="text-xs text-muted-foreground">{uploadProgress}%</p>
            </div>
          ) : (
            <div className="space-y-2">
              <div className="flex justify-center">
                {dragOver ? (
                  <ImageIcon className="w-10 h-10 text-primary" />
                ) : (
                  <Upload className="w-10 h-10 text-muted-foreground" />
                )}
              </div>
              <p className="text-sm text-muted-foreground">
                {dragOver ? 'Drop image here' : 'Click or drag image to upload'}
              </p>
              <p className="text-xs text-muted-foreground">
                JPEG, PNG, WebP, GIF • Max 5MB
              </p>
            </div>
          )}
        </div>
      )}

      {/* Error message */}
      {error && (
        <div className="flex items-center gap-2 p-3 bg-destructive/10 border border-destructive/30 rounded-lg text-sm text-destructive">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Full-screen image preview modal */}
      {showFullPreview && currentImageUrl && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-90"
          onClick={closeFullPreview}
        >
          <button
            type="button"
            onClick={closeFullPreview}
            className="absolute top-4 right-4 p-2 bg-white bg-opacity-20 text-white rounded-full hover:bg-opacity-30 transition-colors"
            title="Close preview"
          >
            <X className="w-6 h-6" />
          </button>
          <img
            src={currentImageUrl}
            alt="Card image full preview"
            className="max-w-[90vw] max-h-[90vh] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}
